import { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import classnames from 'classnames'
import { DndProvider, getBackendOptions, MultiBackend } from '@minoru/react-dnd-treeview'
import { layoutStore, SpinnerLoader } from 'shared/ui'
import {
  type IDropOptions,
  type IInboxTreeCombine,
  type INodeModel,
  TreeRootInboxes,
} from 'entities/Inbox'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { useConversationMenuContext } from 'pages/conversations/ui/ConversationMenu/context'
import { actionTeamsFavoriteDragDrop } from 'pages/conversations/ui/ConversationMenu/ui/ConversationMenuInboxes/Dropdown/Actions/TeamsFavoriteDragDrop'
import { actionMoveToDragDrop } from 'pages/conversations/ui/ConversationMenu/ui/ConversationMenuInboxes/Dropdown/Actions/MoveToDragDrop'
import { actionReorderFavoritesDragDrop } from 'pages/conversations/ui/ConversationMenu/ui/ConversationMenuInboxes/Dropdown/Actions/ReorderFavoritesDragDrop'
import { MenuSectionSmartViewsTree } from 'pages/conversations/ui/ConversationMenu/ui/ConversationMenuInboxes/MenuSectionSmartViewsTree'
import { MenuSectionTree } from './MenuSectionTree/MenuSectionTree'
import { RequestAccessItem } from './RequestAccessItem/RequestAccessItem'
import { MenuQuickFilters } from './MenuQuickFilters/MenuQuickFilters'
import styles from './styles.module.scss'

export const ConversationMenuInboxes = observer(() => {
  const store = useConversationMenuContext()
  const { usersStore } = useEntitiesContext()
  const { favoritesMenuStore, simpleMenuStore, loading, pageLayoutStore } = store
  const isShouldRequestAccess = !!usersStore.user?.isShouldRequestAccess

  const handleDrop = async (
    items: INodeModel<IInboxTreeCombine>[],
    options: IDropOptions<IInboxTreeCombine>,
    dropRoot: TreeRootInboxes
  ) => {
    const { dragSource } = options
    const dragId = dragSource?.id?.toString()
    const dropTargetStr = options.dropTargetId?.toString() ?? ''

    if (
      dragId?.startsWith(dropRoot) &&
      dropTargetStr &&
      !dropTargetStr.startsWith(dropRoot.toString())
    ) {
      return
    }

    await actionTeamsFavoriteDragDrop(options, dropRoot, store)
    await actionMoveToDragDrop(options, dropRoot, store)
    await actionReorderFavoritesDragDrop(items, options, dropRoot, store)
  }
  const itemsFavoritesCount = favoritesMenuStore.selectInboxesCount
  const itemsInboxesCount = simpleMenuStore.selectInboxesCount

  const hideFavoritesInboxes = favoritesMenuStore.hideInboxes
  const hideSimpleInboxes = simpleMenuStore.hideInboxes

  useEffect(() => {
    if (isShouldRequestAccess) {
      store.setLoadingUser(false)
    }
  }, [isShouldRequestAccess])

  if (loading) {
    return (
      <div className={classnames(styles.loader)}>
        <SpinnerLoader ariaLabel={'ConversationMenuInboxes'} />
      </div>
    )
  }

  if (isShouldRequestAccess) return <RequestAccessItem />

  const onClickItemTree = () => {
    if (layoutStore.isMediumView) {
      pageLayoutStore.handleChangeCollapse(true)
    }
  }

  return (
    <DndProvider backend={MultiBackend} options={getBackendOptions()}>
      <MenuQuickFilters />

      <div className={styles.wrap}>
        {!hideFavoritesInboxes && (
          <MenuSectionTree
            items={favoritesMenuStore.items}
            count={itemsFavoritesCount}
            title='Favorites'
            root={TreeRootInboxes.FAVORITE}
            useSearch={{
              handleSearch: favoritesMenuStore.handleUpdateSearch,
              search: favoritesMenuStore.search,
              placeholder: 'Search favorites',
              autofocus: true,
              variant: 'transparent',
            }}
            handleDrop={handleDrop}
            onClickItemTree={onClickItemTree}
          />
        )}

        <MenuSectionSmartViewsTree root={TreeRootInboxes.SMART_VIEW} handleDrop={handleDrop} />

        {!hideSimpleInboxes && (
          <MenuSectionTree
            items={simpleMenuStore.items}
            count={itemsInboxesCount}
            title='Inboxes'
            root={TreeRootInboxes.SIMPLE}
            handleDrop={handleDrop}
            onClickItemTree={onClickItemTree}
            useSearch={{
              handleSearch: simpleMenuStore.handleUpdateSearch,
              search: simpleMenuStore.search,
              placeholder: 'Search inboxes',
              autofocus: true,
              variant: 'transparent',
            }}
          />
        )}
      </div>
    </DndProvider>
  )
})
